import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';

const formatNum = (n) => { if (!n) return '-'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(n>=1e4?0:1)+'K'; return String(n); };
const formatPct = (v) => v != null ? (parseFloat(v)*100).toFixed(1)+'%' : '-';
const formatCurrency = (n) => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const tierCls = (t) => t===1?'bg-amber-500/20 text-amber-400':t===2?'bg-zinc-400/20 text-zinc-300':t===3?'bg-orange-800/20 text-orange-400':'bg-zinc-800 text-zinc-600';
const statusOpts = ['proposed','approved','declined','booked'];
const statusCls = { proposed:'bg-zinc-700 text-zinc-300', approved:'bg-green-500/15 text-green-400', declined:'bg-red-500/15 text-red-400', booked:'bg-blue-500/15 text-blue-400' };

const BASE_AGE_RANGES = [
  { id: 'u18',   label: 'Under 18' },
  { id: '18_24', label: '18-24' },
  { id: '25_34', label: '25-34' },
  { id: '35_44', label: '35-44' },
  { id: '45_54', label: '45-54' },
  { id: '55p',   label: '55+' },
];

// Default grouping: each range is its own group
const defaultAgeGroups = () => BASE_AGE_RANGES.map(r => ({ id: r.id, label: r.label, ranges: [r.id] }));

const AgeSettingsModal = ({ groups, onSave, onClose }) => {
  const [localGroups, setLocalGroups] = useState(() => groups && groups.length ? groups : defaultAgeGroups());
  const dragItem = useRef(null);
  const dragTarget = useRef(null);

  // All ranges currently assigned somewhere
  const usedRanges = new Set(localGroups.flatMap(g => g.ranges));
  // Available to merge — ranges already used
  const allRanges = BASE_AGE_RANGES;

  const updateLabel = (idx, val) => {
    setLocalGroups(prev => prev.map((g, i) => i === idx ? { ...g, label: val } : g));
  };

  const mergeDown = (idx) => {
    // Merge group at idx+1 into group at idx
    if (idx >= localGroups.length - 1) return;
    setLocalGroups(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ranges: [...next[idx].ranges, ...next[idx + 1].ranges],
        label: next[idx].label + ' + ' + next[idx + 1].label };
      next.splice(idx + 1, 1);
      return next;
    });
  };

  const splitGroup = (idx) => {
    // Split last range off the group into its own group
    if (localGroups[idx].ranges.length <= 1) return;
    setLocalGroups(prev => {
      const next = [...prev];
      const ranges = [...next[idx].ranges];
      const split = ranges.pop();
      const splitLabel = BASE_AGE_RANGES.find(r => r.id === split)?.label || split;
      next[idx] = { ...next[idx], ranges, label: ranges.map(r => BASE_AGE_RANGES.find(b => b.id === r)?.label || r).join(' + ') };
      next.splice(idx + 1, 0, { id: split, label: splitLabel, ranges: [split] });
      return next;
    });
  };

  const resetGroups = () => setLocalGroups(defaultAgeGroups());

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragTarget.current = idx; };
  const handleDragEnd = () => {
    const from = dragItem.current, to = dragTarget.current;
    if (from === null || to === null || from === to) return;
    setLocalGroups(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    dragItem.current = null; dragTarget.current = null;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-white font-semibold text-lg">Age Range Settings</h2>
            <p className="text-zinc-500 text-xs mt-0.5">Drag to reorder · Merge adjacent ranges · Edit labels</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="space-y-2 mb-5">
          {localGroups.map((group, idx) => (
            <div key={idx}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 cursor-grab active:cursor-grabbing group">
              <span className="text-zinc-500 text-sm select-none">⠿</span>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex flex-wrap gap-1">
                  {group.ranges.map(r => (
                    <span key={r} className="text-xs bg-nextide-turquoise/20 text-nextide-turquoise px-2 py-0.5 rounded-full">
                      {BASE_AGE_RANGES.find(b => b.id === r)?.label || r}
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={group.label}
                  onChange={e => updateLabel(idx, e.target.value)}
                  onClick={e => e.stopPropagation()}
                  className="ml-auto bg-zinc-700 border border-zinc-600 rounded px-2 py-0.5 text-white text-xs w-28 focus:outline-none focus:border-nextide-turquoise"
                  placeholder="Label..."
                />
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {idx < localGroups.length - 1 && (
                  <button onClick={() => mergeDown(idx)} title="Merge with next"
                    className="text-xs text-zinc-400 hover:text-nextide-turquoise px-1.5 py-0.5 rounded bg-zinc-700">↓+</button>
                )}
                {group.ranges.length > 1 && (
                  <button onClick={() => splitGroup(idx)} title="Split last range off"
                    className="text-xs text-zinc-400 hover:text-yellow-400 px-1.5 py-0.5 rounded bg-zinc-700">split</button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button onClick={resetGroups} className="text-zinc-500 hover:text-zinc-300 text-xs">Reset to defaults</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
            <button onClick={() => onSave(localGroups)} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TalentListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [deals, setDeals] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [listSort, setListSort] = useState({ field: null, dir: 'desc' });
  const [showEstimator, setShowEstimator] = useState(false);
  const [estConfig, setEstConfig] = useState({ omni_impressions: '0', frame_impressions: '0', geo: 'all' });
  const [estimates, setEstimates] = useState(null);
  const [excludedCreators, setExcludedCreators] = useState(new Set());
  const [creatorStreamHrs, setCreatorStreamHrs] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [ageGroups, setAgeGroups] = useState(null);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => { fetchList(); fetchDeals(); }, [id]);

  const fetchList = async () => {
    try {
      const res = await api.get(`/talent-lists/${id}`);
      setList(res.data.list); setItems(res.data.items || []);
      setEditForm({ name: res.data.list.name, deal_id: res.data.list.deal_id || '', description: res.data.list.description || '' });
      if (res.data.list.list_settings?.ageGroups) setAgeGroups(res.data.list.list_settings.ageGroups);
    } catch (e) { navigate('/talent-lists'); }
    finally { setLoading(false); }
  };

  const fetchDeals = async () => { try { const r = await api.get('/deals'); setDeals(r.data.deals||[]); } catch(e){} };

  const saveList = async () => {
    try { await api.put(`/talent-lists/${id}`, editForm); setEditing(false); fetchList(); }
    catch (e) { alert('Failed'); }
  };

  const saveSettings = async (newAgeGroups) => {
    try {
      const settings = { ...(list.list_settings || {}), ageGroups: newAgeGroups };
      await api.put(`/talent-lists/${id}/settings`, { settings });
      setAgeGroups(newAgeGroups);
      setShowSettings(false);
    } catch (e) { alert('Failed to save settings'); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/talent-lists/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${list.name.replace(/[^a-zA-Z0-9 ]/g,'')}_talent_list.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert('Export failed'); }
    finally { setExporting(false); }
  };

  const fetchEstimate = async () => {
    try {
      const res = await api.get(`/talent-lists/${id}/cost-estimate`);
      setEstimates(res.data);
    } catch (e) { console.error('Estimate error:', e); }
  };

  const updateItem = async (itemId, updates) => {
    try {
      await api.put(`/talent-lists/${id}/items/${itemId}`, updates);
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i));
    } catch (e) { alert('Failed to update'); }
  };

  const removeItem = async (itemId) => {
    try { await api.delete(`/talent-lists/${id}/items/${itemId}`); setItems(prev => prev.filter(i => i.id !== itemId)); }
    catch (e) { alert('Failed'); }
  };

  // Drag and drop
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOver.current = idx; };
  const handleDragEnd = async () => {
    const from = dragItem.current;
    const to = dragOver.current;
    if (from === null || to === null || from === to) return;
    const reordered = [...items];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setItems(reordered);
    dragItem.current = null; dragOver.current = null;
    try { await api.put(`/talent-lists/${id}/reorder`, { item_ids: reordered.map(i => i.id) }); }
    catch (e) { fetchList(); }
  };

  if (loading) return <div className="p-8 text-zinc-400">Loading...</div>;
  if (!list) return null;

  return (
    <div className="p-8 min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-4 mb-1">
            <Link to="/talent" className="text-zinc-400 hover:text-white text-sm">Network</Link>
            <Link to="/talent-lists" className="text-zinc-400 hover:text-white text-sm">Lists</Link>
            <span className="text-white text-sm font-semibold">{list.name}</span>
          </div>
          {editing ? (
            <div className="space-y-2 mt-2">
              <input type="text" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white font-bold text-lg" />
              <div className="flex gap-2">
                <select value={editForm.deal_id} onChange={e => setEditForm({...editForm, deal_id: e.target.value})}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">No linked deal</option>
                  {deals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <button onClick={saveList} className="bg-nextide-turquoise text-black px-3 py-2 rounded-lg text-sm font-semibold">Save</button>
                <button onClick={() => setEditing(false)} className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="text-2xl font-bold text-white">{list.name}</h1>
              <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1">
                <span>{items.length} creator{items.length !== 1 ? 's' : ''}</span>
                {list.deal_name && <span>Linked to: <Link to={`/pipeline/${list.deal_id}`} className="text-nextide-turquoise hover:underline">{list.deal_name}</Link></span>}
                {ageGroups && ageGroups.length > 0 && (
                  <button onClick={() => setShowSettings(true)} className="text-xs text-zinc-500 hover:text-zinc-300">
                    {ageGroups.map(g => g.label).join(' · ')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!editing && <button onClick={() => setEditing(true)} className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm">Edit</button>}
          <button onClick={() => setShowSettings(true)} title="List Settings"
            className="bg-zinc-800 text-zinc-400 hover:text-white px-3 py-2 rounded-lg text-sm">⚙️</button>
          <button onClick={() => { setShowEstimator(!showEstimator); if (!showEstimator && !estimates) fetchEstimate(); }}
            className={`px-3 py-2 rounded-lg text-sm ${showEstimator ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400'}`}>Forecast</button>
          <button onClick={handleExport} disabled={exporting || items.length === 0}
            className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {exporting ? 'Exporting...' : 'Export .xlsx'}
          </button>
        </div>
      </div>

      {/* Budget Calculator */}
      {/* Forecast & Cost Estimator */}
      {showEstimator && estimates && (() => {
        const geo = estConfig.geo;
        const geoMultiplier = (c) => {
          if (geo === 'all') return 1;
          const pct = parseFloat(c[`aud_${geo}`]) || 0;
          return pct;
        };
        const activeCreators = estimates.creators.filter(c => !excludedCreators.has(c.id));
        const omniImpr = parseInt(estConfig.omni_impressions) || 0;
        const frameImpr = parseInt(estConfig.frame_impressions) || 0;
        const MONTHLY_STREAM_HRS = 160;

        // Monthly avails per creator = (CCV * 12) * 160hrs * geoMultiplier
        const creatorsWithAvails = activeCreators.map(c => {
          const geoMult = geoMultiplier(c);
          const rawAvails = (c.ccv * 12) * MONTHLY_STREAM_HRS;
          return { ...c, monthlyAvails: Math.round(rawAvails * geoMult), geoMult };
        });
        const totalMonthlyAvails = creatorsWithAvails.reduce((s, c) => s + c.monthlyAvails, 0);
        const totalGoalImpr = omniImpr + frameImpr;
        const canDeliver = totalGoalImpr === 0 || totalMonthlyAvails >= totalGoalImpr;

        const creatorsWithOmni = creatorsWithAvails.filter(c => c.omniCpm);
        const creatorsWithFrame = creatorsWithAvails.filter(c => c.frameCpm);
        const avgOmniCpm = creatorsWithOmni.length > 0 ? creatorsWithOmni.reduce((s,c) => s+c.omniCpm, 0)/creatorsWithOmni.length : 0;
        const avgFrameCpm = creatorsWithFrame.length > 0 ? creatorsWithFrame.reduce((s,c) => s+c.frameCpm, 0)/creatorsWithFrame.length : 0;
        const omniBudget = omniImpr > 0 && avgOmniCpm > 0 ? (omniImpr/1000)*avgOmniCpm : 0;
        const frameBudget = frameImpr > 0 && avgFrameCpm > 0 ? (frameImpr/1000)*avgFrameCpm : 0;
        const streamBudget = creatorsWithAvails.reduce((s,c) => {
          const hrs = parseFloat(creatorStreamHrs[c.id]) || 0;
          return s + (c.streamPerHour || 0) * hrs;
        }, 0);
        const streamCreatorCount = creatorsWithAvails.filter(c => parseFloat(creatorStreamHrs[c.id]) > 0).length;
        const totalStreamHrs = creatorsWithAvails.reduce((s,c) => s + (parseFloat(creatorStreamHrs[c.id]) || 0), 0);
        const totalBudget = omniBudget + frameBudget + streamBudget;

        const toggleCreator = (id) => {
          setExcludedCreators(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          });
        };

        return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-white font-semibold text-sm">Forecast & Cost Estimator</h3>
            <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded">INTERNAL</span>
          </div>

          {/* Geo toggle */}
          <div className="flex gap-1 mb-4">
            {[['all','All Regions'],['usa','USA'],['canada','Canada'],['uk','UK'],['australia','Australia']].map(([key, label]) => (
              <button key={key} onClick={() => setEstConfig(prev => ({...prev, geo: key}))}
                className={`px-3 py-1 rounded text-xs ${geo === key ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>{label}</button>
            ))}
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><label className="block text-xs text-zinc-400 mb-1">OmniBanner Goal Impressions</label>
              <input type="number" step="100000" value={estConfig.omni_impressions} onChange={e => setEstConfig({...estConfig, omni_impressions: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm" /></div>
            <div><label className="block text-xs text-zinc-400 mb-1">Immersive Frames Goal Impressions</label>
              <input type="number" step="100000" value={estConfig.frame_impressions} onChange={e => setEstConfig({...estConfig, frame_impressions: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm" /></div>
          </div>

          {/* Inventory check */}
          {totalGoalImpr > 0 && (
            <div className={`rounded-lg px-4 py-3 mb-4 ${canDeliver ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs"><span className={canDeliver ? 'text-green-400' : 'text-red-400'}>{canDeliver ? '✓' : '✗'} Inventory Check</span>
                  <span className="text-zinc-400 ml-2">Goal: {totalGoalImpr.toLocaleString()} impr.{geo !== 'all' ? ` (${geo.toUpperCase()})` : ''}</span></div>
                <div className="text-xs"><span className="text-zinc-400">List Monthly Avails{geo !== 'all' ? ` (${geo.toUpperCase()})` : ''}: </span>
                  <span className="text-white font-semibold">{totalMonthlyAvails >= 1e6 ? (totalMonthlyAvails/1e6).toFixed(1)+'M' : totalMonthlyAvails.toLocaleString()}</span>
                  <span className={`ml-2 font-semibold ${canDeliver ? 'text-green-400' : 'text-red-400'}`}>{totalGoalImpr > 0 ? (totalMonthlyAvails/totalGoalImpr*100).toFixed(0)+'% coverage' : ''}</span></div>
              </div>
            </div>
          )}

          {/* Creator toggle info */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-zinc-500 text-xs">{activeCreators.length} of {estimates.creators.length} creators active <span className="text-zinc-600">— click to toggle</span></div>
            {excludedCreators.size > 0 && <button onClick={() => setExcludedCreators(new Set())} className="text-nextide-turquoise text-xs hover:underline">Reset all</button>}
          </div>

          {/* Per-creator table */}
          <table className="w-full mb-3">
            <thead><tr className="text-xs text-zinc-500 border-b border-zinc-800">
              <th className="text-left pb-1 w-6"></th>
              <th className="text-left pb-1">Creator</th><th className="text-left pb-1 w-10">Tier</th>
              <th className="text-right pb-1">CCV</th><th className="text-right pb-1">Monthly Avails{geo !== 'all' ? ` (${geo.toUpperCase()})` : ''}</th>
              <th className="text-right pb-1">Omni CPM</th><th className="text-right pb-1">Frame CPM</th><th className="text-right pb-1">Stream $/hr</th><th className="text-right pb-1">Stream Hrs</th><th className="text-right pb-1">Stream Cost</th>
            </tr></thead>
            <tbody>{estimates.creators.map(c => {
              const excluded = excludedCreators.has(c.id);
              const avails = creatorsWithAvails.find(ca => ca.id === c.id);
              return (
              <tr key={c.id} onClick={() => toggleCreator(c.id)} className={`text-xs border-b border-zinc-800/50 cursor-pointer ${excluded ? 'opacity-30' : 'hover:bg-zinc-800/30'}`}>
                <td className="py-1.5 text-center">{excluded ? <span className="text-zinc-600">○</span> : <span className="text-nextide-turquoise">●</span>}</td>
                <td className="py-1.5 text-white font-medium">{c.name}</td>
                <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded font-semibold ${c.talent_tier===1?'bg-amber-500/20 text-amber-400':c.talent_tier===2?'bg-zinc-400/20 text-zinc-300':'bg-orange-800/20 text-orange-400'}`}>T{c.talent_tier||'-'}</span></td>
                <td className="py-1.5 text-purple-400 text-right">{c.ccv > 0 ? c.ccv.toLocaleString() : '-'}</td>
                <td className="py-1.5 text-zinc-300 text-right">{avails?.monthlyAvails > 0 ? (avails.monthlyAvails>=1e6?(avails.monthlyAvails/1e6).toFixed(1)+'M':avails.monthlyAvails.toLocaleString()) : '-'}</td>
                <td className="py-1.5 text-right">{c.omniCpm ? <><span className="text-white">${c.omniCpm.toFixed(2)}</span> <span className={`text-xs ${c.omniSource==='card'?'text-green-400':'text-yellow-400'}`}>{c.omniSource==='card'?'Card':'Avg'}</span></> : <span className="text-zinc-600">-</span>}</td>
                <td className="py-1.5 text-right">{c.frameCpm ? <><span className="text-white">${c.frameCpm.toFixed(2)}</span> <span className={`text-xs ${c.frameSource==='card'?'text-green-400':'text-yellow-400'}`}>{c.frameSource==='card'?'Card':'Avg'}</span></> : <span className="text-zinc-600">-</span>}</td>
                <td className="py-1.5 text-right">{c.streamPerHour ? <span className="text-white">${c.streamPerHour.toLocaleString()}</span> : <span className="text-zinc-600">-</span>}</td>
                <td className="py-1.5 text-right" onClick={e => e.stopPropagation()}>
                  <input type="number" step="0.5" min="0" value={creatorStreamHrs[c.id] || ''}
                    onChange={e => setCreatorStreamHrs(prev => ({...prev, [c.id]: e.target.value}))}
                    placeholder="0"
                    className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-white text-xs w-14 text-right focus:outline-none focus:border-nextide-turquoise" />
                </td>
                <td className="py-1.5 text-right">{(() => {
                  const hrs = parseFloat(creatorStreamHrs[c.id]) || 0;
                  const cost = (c.streamPerHour || 0) * hrs;
                  return cost > 0 ? <span className="text-nextide-turquoise font-medium">${cost.toLocaleString()}</span> : <span className="text-zinc-600">-</span>;
                })()}</td>
              </tr>);
            })}</tbody>
          </table>

          {/* Budget summary */}
          <div className="border-t border-zinc-700 pt-3 space-y-2">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-zinc-800 rounded-lg p-3"><div className="text-zinc-400 text-xs">OmniBanner Budget</div>
                <div className="text-white font-bold">{omniBudget > 0 ? '$'+omniBudget.toLocaleString(undefined,{maximumFractionDigits:0}) : '-'}</div>
                <div className="text-zinc-600 text-xs">{omniImpr > 0 ? omniImpr.toLocaleString()+' impr × $'+avgOmniCpm.toFixed(2)+' avg CPM' : 'No goal set'}</div></div>
              <div className="bg-zinc-800 rounded-lg p-3"><div className="text-zinc-400 text-xs">Immersive Frames Budget</div>
                <div className="text-white font-bold">{frameBudget > 0 ? '$'+frameBudget.toLocaleString(undefined,{maximumFractionDigits:0}) : '-'}</div>
                <div className="text-zinc-600 text-xs">{frameImpr > 0 ? frameImpr.toLocaleString()+' impr × $'+avgFrameCpm.toFixed(2)+' avg CPM' : 'No goal set'}</div></div>
              <div className="bg-zinc-800 rounded-lg p-3"><div className="text-zinc-400 text-xs">Sponsored Streams Budget</div>
                <div className="text-white font-bold">{streamBudget > 0 ? '$'+streamBudget.toLocaleString(undefined,{maximumFractionDigits:0}) : '-'}</div>
                <div className="text-zinc-600 text-xs">{streamCreatorCount > 0 ? streamCreatorCount+' creator'+(streamCreatorCount!==1?'s':'')+' × '+totalStreamHrs+' total hrs' : 'Assign hours per creator above'}</div></div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-nextide-turquoise">
              <div className="text-zinc-400 text-sm">Total Estimated Budget</div>
              <div className="text-nextide-turquoise text-2xl font-bold">${totalBudget.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
            </div>
            <div className="text-zinc-600 text-xs">Monthly avails = (CCV × 12) × 160hrs{geo !== 'all' ? ` × ${geo.toUpperCase()} %` : ''} per creator</div>
          </div>
        </div>);
      })()}

      {/* Items Table */}

      {items.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-zinc-500 text-lg mb-2">No creators in this list</div>
          <div className="text-zinc-600 text-sm">Go to <Link to="/talent" className="text-nextide-turquoise hover:underline">Network</Link> to select and add talent</div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="w-8 p-3">{listSort.field && <button onClick={() => setListSort({field:null,dir:'desc'})} className="text-nextide-turquoise text-xs" title="Reset to drag order">↺</button>}</th>
                {[
                  { field: 'name', label: 'Creator', align: 'text-left' },
                  { field: 'talent_tier', label: 'Tier', align: 'text-left' },
                  { field: 'brand_safety_score', label: 'BSS', align: 'text-left' },
                  { field: 'primary_category', label: 'Category', align: 'text-left' },
                  { field: 'total_following', label: 'Total', align: 'text-right' },
                  { field: 'twitch_avg_ccv', label: 'CCV', align: 'text-right' },
                ].map(col => (
                  <th key={col.field} onClick={() => {
                    if (listSort.field === col.field) setListSort({field: col.field, dir: listSort.dir === 'desc' ? 'asc' : 'desc'});
                    else setListSort({field: col.field, dir: 'desc'});
                  }} className={`${col.align} p-3 text-zinc-400 text-xs font-medium cursor-pointer hover:text-white select-none`}>
                    {col.label} {listSort.field === col.field && <span className="text-nextide-turquoise">{listSort.dir === 'desc' ? '↓' : '↑'}</span>}
                  </th>
                ))}
                <th className="text-left p-3 text-zinc-400 text-xs font-medium">Status</th>
                <th className="text-left p-3 text-zinc-400 text-xs font-medium">Notes</th>
                <th className="w-10 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {(listSort.field ? [...items].sort((a, b) => {
                let av = a[listSort.field], bv = b[listSort.field];
                if (listSort.field === 'name') { av = (av||'').toLowerCase(); bv = (bv||'').toLowerCase(); return listSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
                av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
                return listSort.dir === 'desc' ? bv - av : av - bv;
              }) : items).map((item, idx) => (
                <tr key={item.id}
                  draggable={!listSort.field}
                  onDragStart={() => !listSort.field && handleDragStart(idx)}
                  onDragEnter={() => !listSort.field && handleDragEnter(idx)}
                  onDragEnd={!listSort.field ? handleDragEnd : undefined}
                  onDragOver={e => !listSort.field && e.preventDefault()}
                  className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${listSort.field ? '' : 'cursor-grab active:cursor-grabbing'}`}>
                  <td className="p-3 text-zinc-600 text-xs select-none">{idx + 1}</td>
                  <td className="p-3">
                    <Link to={`/talent/${item.talent_id}`} className="text-white font-medium text-sm hover:text-nextide-turquoise" onClick={e => e.stopPropagation()}>
                      {item.name}
                    </Link>
                    {item.conflict_warnings && item.conflict_warnings.length > 0 && (
                      <div className="text-red-400 text-xs mt-0.5" title={item.conflict_warnings.join(', ')}>Conflict detected</div>
                    )}
                  </td>
                  <td className="p-3"><span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(item.talent_tier)}`}>T{item.talent_tier || '-'}</span></td>
                  <td className="p-3 text-xs">{item.brand_safety_score ? parseFloat(item.brand_safety_score).toFixed(1) : '-'}</td>
                  <td className="p-3 text-zinc-400 text-xs max-w-[150px] truncate">{item.primary_category || '-'}</td>
                  <td className="p-3 text-right text-nextide-turquoise text-xs">{formatNum(item.total_following)}</td>
                  <td className="p-3 text-right text-purple-400 text-xs">{item.twitch_avg_ccv > 0 ? formatNum(item.twitch_avg_ccv) : '-'}</td>
                  <td className="p-3">
                    <select value={item.status} onChange={e => updateItem(item.id, { status: e.target.value })}
                      className={`px-2 py-0.5 rounded text-xs capitalize border-0 cursor-pointer ${statusCls[item.status] || 'bg-zinc-800 text-zinc-400'}`}>
                      {statusOpts.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <input type="text" value={item.list_notes || ''} placeholder="Notes..."
                      onChange={e => setItems(prev => prev.map(i => i.id === item.id ? {...i, list_notes: e.target.value} : i))}
                      onBlur={e => updateItem(item.id, { list_notes: e.target.value })}
                      className="bg-transparent border-0 text-zinc-300 text-xs w-full min-w-[100px] focus:outline-none focus:bg-zinc-800 rounded px-1" />
                  </td>
                  <td className="p-3">
                    <button onClick={() => removeItem(item.id)} className="text-zinc-600 hover:text-red-400 text-xs">x</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSettings && (
        <AgeSettingsModal
          groups={ageGroups}
          onSave={saveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default TalentListDetail;
